import { ArrowIcon } from "~/assets/icons";
import styles from "./PrimarySelectInput.module.scss";
import classNames from "classnames/bind";
import { useEffect, useRef, useState } from "react";
const cx = classNames.bind(styles);

type SelectOption = { label: string; value: string | number };

type PrimarySelectInputProps = {
  label?: string;
  placeholder?: string;
  value?: string | string[];
  name: string;
  onChange?: (value: any) => void;
  errorMessage?: string;
  className?: string;
  options?: SelectOption[];
  disabled?: boolean;
  classNameIcon?: string;
  classNameList?: string;
  multiple?: boolean;
};

const PrimarySelectInput = (props: PrimarySelectInputProps) => {
  const {
    label,
    placeholder,
    value,
    name,
    onChange,
    errorMessage,
    className,
    options,
    disabled = false,
    classNameIcon,
    classNameList,
    multiple = false,
  } = props;
  const [showList, setShowList] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);

  const selectedValues = multiple
    ? (Array.isArray(value) ? value : [])
    : [];

  const getDisplayValue = () => {
    if (multiple) {
      if (selectedValues.length === 0) {
        return placeholder;
      }

      const labels = options
        ?.filter((option) =>
          selectedValues.includes(String(option.value))
        )
        .map((option) => option.label);

      return labels?.length ? labels.join(", ") : placeholder;
    }

    if (!value || Array.isArray(value)) {
      return placeholder;
    }

    return (
      options?.find((option) => String(option.value) === value)?.label || value
    );
  };

  const hasSelection = multiple
    ? selectedValues.length > 0
    : !!value && !Array.isArray(value);

  useEffect(() => {
    if (showList) {
      isScrollingRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (dropdownRef.current) {
            dropdownRef.current.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "nearest",
            });
          } else if (selectRef.current) {
            selectRef.current.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "nearest",
            });
          }
          setTimeout(() => {
            isScrollingRef.current = false;
          }, 600);
        });
      });
    }
  }, [showList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setShowList(false);
      }
    };

    const handleScroll = (event: Event) => {
      if (showList && !isScrollingRef.current) {
        const target = event.target as Node;
        if (
          dropdownRef.current &&
          (dropdownRef.current === target || dropdownRef.current.contains(target))
        ) {
          return;
        }
        setShowList(false);
      }
    };

    const handleWheel = (event: WheelEvent) => {
      if (showList && !isScrollingRef.current) {
        const target = event.target as Node;
        if (
          dropdownRef.current &&
          (dropdownRef.current === target || dropdownRef.current.contains(target))
        ) {
          return;
        }
        setShowList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("wheel", handleWheel, {
      passive: true,
      capture: true,
    });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, [showList]);

  const handleOptionSelect = (optionValue: string) => {
    if (multiple) {
      const normalizedValue = String(optionValue);
      const nextValues = selectedValues.includes(normalizedValue)
        ? selectedValues.filter((item) => item !== normalizedValue)
        : [...selectedValues, normalizedValue];

      onChange?.(nextValues);
      return;
    }

    setShowList(false);
    onChange?.(optionValue);
  };

  const isOptionSelected = (option: SelectOption) => {
    if (multiple) {
      return selectedValues.includes(String(option.value));
    }

    if (Array.isArray(value)) {
      return false;
    }

    return option.label === value || String(option.value) === value;
  };

  return (
    <div
      className={cx("primary-select-input-container", className)}
      ref={selectRef}
    >
      {label && (
        <label htmlFor={name} className={cx("input-label")}>
          {label}
        </label>
      )}
      <button
        type="button"
        className={cx("select-container", { error: !!errorMessage, disabled })}
        onClick={() => {
          if (!disabled) {
            setShowList(!showList);
          }
        }}
        disabled={disabled}
      >
        <h3 className={cx("select-value", { "has-value": !hasSelection })}>
          {getDisplayValue()}
        </h3>
        <ArrowIcon
          className={cx("arrow-icon", { rotated: showList }, classNameIcon)}
        />
      </button>

      {showList && !disabled && (
        <div
          ref={dropdownRef}
          className={cx("dropdown-list", classNameList)}
        >
          {options?.map((option, index) => (
            <div
              key={index}
              className={cx("dropdown-item", {
                selected: isOptionSelected(option),
              })}
              onClick={() => handleOptionSelect(String(option.value))}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
      {errorMessage && <p className={cx("error-message")}>{errorMessage}</p>}
    </div>
  );
};

export default PrimarySelectInput;
